import { FC, useContext, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Hidden,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  PiFile,
  PiGlobe,
  PiTelegramLogoFill,
  PiTwitterLogoFill,
} from "react-icons/pi";

import LinearProgressBar from "./LinearProgress";
import PoolDetailStyle from "./DetailsStyle";

import LandingBg from "../../assets/images/landing_bg.png";
import { Colors } from "../../helpers/theme";
import dayHourMinute from "../../helpers/MomentDHM";
import { AiFillWarning, AiOutlineInfoCircle } from "react-icons/ai";
import PoolContext from "../../store/Pool/PoolContext";
import WalletContext from "../../store/Wallet/WalletContext";
import { calculateProgress } from "../../helpers/PoolsProgressCalc";
import PoolButton from "../Projects/PoolButton";
import { useHistory } from "react-router";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import PoolDetailCardSkeleton from "./ProjectDetailCardSkeleton";
import AuthContext from "../../store/Auth/AuthContext";
import SubscriptionContext from "../../store/Subscription/SubscriptionContext";
import GiveawayLabel from "../Projects/GiveawayLabel";
import { appConfig } from "../../config";
import { FaInfoCircle } from "react-icons/fa";
import QuizVideosModal from "./QuizVideos";

type PoolDetailCardProps = {
  poolData: any;
  getWhitelisted: any;
  whitelistedCompleted: any;
  investBtn: any;
  failed: boolean;
  isExternalProject?: boolean;
  openWhitelistExternalModal: any;
  showCountryModal: () => void;
  code?: string;
};

const PoolDetailCard: FC<any> = ({
  poolData,
  getWhitelisted,
  whitelistedCompleted,
  failed,
  investBtn,
  isExternalProject = false,
  openWhitelistExternalModal,
  showCountryModal,
  code,
}: PoolDetailCardProps) => {
  const classes = PoolDetailStyle();
  const history = useHistory();

  const {
    setPoolProgressDetails,
    poolProgress,
    getPoolProgressDetails,
    setActivePool,
    isGiveaway,
  } = useContext<any>(PoolContext);
  const { getContributionDetails, hasKycWallet, wallets } =
    useContext<any>(WalletContext);
  const { isAuth, getProfileData } = useContext<any>(AuthContext);
  const { lockedGains } = useContext<any>(SubscriptionContext);

  const [chipColor, setColor] = useState<string>("default");
  const [poolStatusText, setPoolStatusText] = useState<string | null>(null);
  const [tooltipText, setTooltipText] = useState<string>("");
  const [timer, setTimer] = useState();
  const [liveTimer, setLiveTimer] = useState<any>("");
  const [alert, toggleAlert] = useState<boolean>(false);
  const [hasFailed, setFailed] = useState<boolean>(false);
  const [totalRaised, setTotalRaised] = useState(0);
  const [maxRaise, setMaxRaise] = useState(0);
  const [progressval, setProgressval] = useState(0);
  const [duration, setDuration] = useState(0);
  const [poolProgressError, setPoolProgressError] = useState<any>(false);
  const today = new Date();
  const [isGiveawayPool, setGiveawayPool] = useState(false);
  const [showQuizVideoModal, toggleQuizVideoModal] = useState<boolean>(false);

  useEffect(() => {
    const updateTimer = () => {
      setLiveTimer(dayHourMinute(timer));
    };
    updateTimer();
    const timerId = setInterval(updateTimer, 1000);
    return () => clearInterval(timerId);
  }, [timer]);

  useEffect(() => {
    if (isAuth && getProfileData && wallets.length > 0) {
      // Check if user is authenticated and profile data is available
      if (
        poolData &&
        poolData.status === "whitelist_open" &&
        targetDateDifference(poolData.pledgeEndDate) > 0 &&
        ((!isExternalProject && lockedGains >= 1000) || isExternalProject)
      ) {
        if (getProfileData.kycStatus === "approved" && hasKycWallet) {
          toggleAlert(false); // All conditions are met, turn off the alert
        } else {
          toggleAlert(true); // User's KYC status is not approved or hasKycWallet is false
        }
      } else {
        toggleAlert(false); // poolData conditions are not met
      }
    } else {
      toggleAlert(false); // User is not authenticated or profile data is unavailable
    }
  }, [poolData, getProfileData, hasKycWallet, isAuth, lockedGains]);

  useEffect(() => {
    if (poolData) {
      if (isGiveaway(poolData.id)) {
        setGiveawayPool(true);
      }
      setPoolStatusColorTextAndTimer(poolData.status);
      if (poolData.status === "whitelist_open" && failed) {
        setFailed(true);
      }
      if (
        poolData.networks &&
        poolData.networks.length > 0 &&
        poolData.status === "ongoing"
      ) {
        const network = poolData.networks[0];
        const { address, networkId, token } = network;
        getContributionDetails(networkId, address, token)
          .then((res: any) => {
            setPoolProgressDetails(poolData.id, res.cap, res.amount);
          })
          .catch((error: any) => {
            setPoolProgressError(true);
          });
      }

      const contributionEndDate = new Date(poolData.contributionEndDate);
      const difference = contributionEndDate.getTime() - today.getTime();
      const differenceInDays = Math.ceil(difference / (1000 * 3600 * 24));
      setDuration(differenceInDays > 0 ? differenceInDays : 0);
    }
  }, [poolData, isAuth]);

  useEffect(() => {
    if (maxRaise === 0) setProgressval(0);
    setProgressval(calculateProgress(totalRaised!, maxRaise!));
  }, [totalRaised, maxRaise]);

  useEffect(() => {
    if (!poolData) return;
    if (poolProgress && poolProgress.length > 0) {
      const progress = getPoolProgressDetails(poolData.id);
      if (progress) {
        setTotalRaised(progress.amount ?? 0);
        setMaxRaise(progress.cap ?? 0);
      }
    }
  }, [poolData, poolProgress]);

  const targetDateDifference = (targetDateString: string) => {
    const targetDate = new Date(targetDateString);
    const difference = targetDate.getTime() - today.getTime();
    const differenceInDays = Math.ceil(difference / (1000 * 3600 * 24));
    return differenceInDays;
  };

  const setPoolStatusColorTextAndTimer = (status: string) => {
    switch (status) {
      case "ongoing":
        setColor("green");
        setPoolStatusText(null);
        return;
      case "whitelist_open":
        setColor("darkGreen");
        if (
          targetDateDifference(poolData.pledgeEndDate) > 0 &&
          !isExternalProject
        ) {
          setPoolStatusText("Quiz Closes");
          setTimer(poolData.pledgeEndDate);
        } else {
          setPoolStatusText("Live In");
          setTimer(poolData.contributionStartDate);
        }
        setTooltipText(
          "Finish KYC, stake GAINS token and finish the quiz before the deadline to get a chance to invest in the project",
        );
        return;
      case "whitelist_closed":
        setColor("darkGreen");
        if (targetDateDifference(poolData.contributionStartDate) > 0) {
          setPoolStatusText("Live In");
          setTimer(poolData.contributionStartDate);
        } else {
          setPoolStatusText(null);
        }
        setTooltipText(
          "Date when users can start investing in the project. Only users whitelisted for a project can invest.",
        );
        return;
      case "upcoming":
        setColor("orange");
        setTimer(poolData.pledgeStartDate);
        if (targetDateDifference(poolData.pledgeStartDate) > 0) {
          setPoolStatusText("Launches In");
        } else {
          setPoolStatusText("Launches Soon");
        }

        setTooltipText(
          "Date when this project will start accepting pledges from user. Users need to pledge to be able to invest in projects.",
        );
        return;
      case "closed":
        setColor("darkGreen");
        setPoolStatusText(null);
        return;
      case "cancelled":
        setColor("red");
        setPoolStatusText(null);
        return;
      case "refunded":
        setColor("orange");
        setPoolStatusText(null);
        return;
    }
  };

  // TODO: Move this to util ( also used on pool list page )
  const getStatusText = (status: string) => {
    switch (status) {
      case "ongoing":
        return "Live";
      case "whitelist_open":
        return "Whitelist Open";
      case "whitelist_closed":
        return "Whitelist Closed";
      case "upcoming":
        return "Upcoming";
      case "closed":
        return "Closed";
    }
  };

  const LearnScreen = () => {
    history.push(`/learn/${poolData.id}`);
  };

  const InvestScreen = () => {
    let url = `/invest/${poolData.id}` + (code ? `?code=${code}` : "");
    history.push(url, { canInvest: investBtn });
  };

  const PortfolioScreen = () => {
    history.push(`/portfolio`);
  };

  const GainsScreen = () => {
    history.push(`/gains`);
  };

  const handleClick = () => {
    if (poolData.status === "ongoing") {
      InvestScreen();
    } else if (poolData.status === "whitelist_open") {
      if (isExternalProject) {
        if (hasKycWallet && getProfileData?.kycStatus === "approved") {
          openWhitelistExternalModal();
        } else {
          showCountryModal();
        }
      } else if (lockedGains < 1000) GainsScreen();
      else if (
        isGiveawayPool &&
        !isUserEligibleForGiveaway() &&
        hasKycWallet &&
        getProfileData?.kycStatus === "approved"
      )
        GainsScreen();
      else LearnScreen();
    } else if (poolData.status === "closed") {
      toggleQuizVideoModal(true);
    }
  };

  if (!poolData) return <PoolDetailCardSkeleton />;

  const handleBack = (event: any) => {
    event.preventDefault();
    history.goBack();
  };

  const isUserEligibleForGiveaway = () => {
    return (
      getProfileData?.learnpad_tier &&
      appConfig.giveAwayUserTiers.includes(getProfileData?.learnpad_tier)
    );
  };

  return (
    <Card className={classes.cardRoot}>
      <CardActionArea>
        {isGiveawayPool && <GiveawayLabel />}
        <CardMedia
          image={poolData?.banner ? poolData.banner : LandingBg}
          title={poolData?.name} // Change to pool name
          component="img"
          height="140"
        />
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          className={classes.floatContainer}
        >
          <Hidden lgUp>
            <Toolbar>
              <IconButton edge="start" color="inherit" onClick={handleBack}>
                <MdOutlineArrowBackIosNew
                  style={{
                    width: "24px",
                    height: "24px",
                    background: Colors.white,
                    color: Colors.greySlate,
                    borderRadius: "50%",
                    padding: "5px",
                    // boxShadow: "0px 8px 28px 0px #4D92D512, 0px 4px 6px 0px #06092505",
                    boxShadow:
                      "0px 0.33000001311302185px 4px rgba(0, 0, 0, 0.10)",
                  }}
                />
              </IconButton>
              {/* Other toolbar items */}
            </Toolbar>
          </Hidden>
          <Grid
            item
            lg={12}
            sm="auto"
            style={{
              textAlign: "right",
            }}
          >
            <Chip
              label={getStatusText(poolData?.status)}
              className={classes.chip + " " + chipColor}
            />
          </Grid>
        </Grid>
      </CardActionArea>
      <CardContent className={classes.cardContent}>
        <Grid container direction="column">
          <Grid item>
            <Grid container direction="row" alignItems="center">
              <Grid item xs={2}>
                <Avatar className={classes.avatar} src={poolData?.image} />
              </Grid>
              <Grid item xs={5}>
                <Typography className={classes.title}>
                  {poolData?.name}
                </Typography>
                <Typography className={classes.token}>
                  ${poolData?.ticker}
                </Typography>
              </Grid>
              <Grid item xs={5} className={classes.social}>
                <Box className={classes.socialIcons}>
                  <a href={poolData.socialNetworks.website} target="_blank">
                    <PiGlobe />
                  </a>
                </Box>
                <Box className={classes.socialIcons}>
                  <a href={poolData.socialNetworks.telegram} target="_blank">
                    <PiTelegramLogoFill />
                  </a>
                </Box>
                <Box className={classes.socialIcons}>
                  <a href={poolData.socialNetworks.twitter} target="_blank">
                    <PiTwitterLogoFill />
                  </a>
                </Box>
                <Box className={classes.socialIcons}>
                  <a
                    href={poolData?.socialNetworks?.whitePaper}
                    target="_blank"
                  >
                    <PiFile />
                  </a>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Typography className={classes.description}>
              {poolData?.description}
            </Typography>
          </Grid>
          {poolData?.status === "ongoing" && (
            <>
              {poolProgressError ? (
                <Typography
                  component="span"
                  variant="caption"
                  color="secondary"
                  className={classes.progressError}
                >
                  <AiFillWarning />
                  &nbsp;Failed to read from blockchain
                </Typography>
              ) : (
                <Grid item className={classes.progress}>
                  <LinearProgressBar
                    value={progressval}
                    maxRaise={maxRaise}
                    totalRaised={totalRaised}
                    duration={duration}
                    className={classes.progressBar}
                    showFull={true}
                  />
                </Grid>
              )}
            </>
          )}
          {(poolData?.status === "whitelist_open" ||
            poolData?.status === "whitelist_closed" ||
            poolData?.status === "upcoming") && (
            <Grid item>
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                style={{
                  margin: "10px 0px 10px 5px",
                }}
              >
                <Grid item lg={3}>
                  <Typography
                    style={{
                      fontWeight: 600,
                      fontSize: "12px",
                      lineHeight: "14px",
                      color: "#8CA2B7",
                    }}
                  >
                    {poolStatusText}
                    &nbsp;
                    {
                      <Tooltip title={tooltipText}>
                        <IconButton
                          style={{
                            padding: "1px",
                          }}
                        >
                          <AiOutlineInfoCircle size={12} />
                        </IconButton>
                      </Tooltip>
                    }
                  </Typography>
                </Grid>
                {liveTimer && (
                  <Grid item lg={9}>
                    <Typography
                      component="span"
                      className="fs"
                      variant="h4"
                      style={{
                        color: "#8CA2B7",
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      {liveTimer}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
          {alert && (
            <Grid
              item
              style={{
                margin: "10px 0px 5px 0px",
              }}
            >
              <Alert
                variant="filled"
                severity="error"
                className={`${classes.alert} ${getProfileData?.kycStatus ? (getProfileData?.kycStatus === "approved" ? (hasKycWallet ? getProfileData?.kycStatus : "incomplete") : getProfileData?.kycStatus) : "unverified"}`}
                icon={<AiFillWarning />}
                onClick={() => history.push("/profile-settings/0")}
              >
                {!getProfileData?.kycStatus
                  ? "Submit your KYC"
                  : getProfileData?.kycStatus === "approved" && !hasKycWallet
                    ? "KYC data incomplete"
                    : "KYC verification pending"}
              </Alert>
            </Grid>
          )}
          {isGiveawayPool && poolData.status === "whitelist_open" && !alert && (
            <Grid item>
              <Typography className={classes.backers}>
                Giveaway pools are open only to the top 2 tiers i.e.{" "}
                <span>Whales and Sharks</span>.{" "}
                {isUserEligibleForGiveaway() ? (
                  " You are eligible to participate in this giveaway pool."
                ) : (
                  <p style={{ display: "inline" }}>
                    Stake additional{" "}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: `${25000 - lockedGains} GAINS`,
                      }}
                    />{" "}
                    at least to upgrade to the top 2 tiers to participate.
                  </p>
                )}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
      <CardActions className={classes.cardAction}>
        <Grid container direction="column" justifyContent="center">
          <Grid item>
            <PoolButton
              poolId={poolData.id}
              poolStatus={poolData.status}
              dealPage={false}
              handleClick={handleClick}
              whitelisted={getWhitelisted}
              whitelistedData={whitelistedCompleted}
              quizStatus={failed}
              kycDeadlineDate={poolData.kycDeadlineDate}
              invest={investBtn}
              pledgeStartDate={poolData.pledgeStartDate}
              pledgeEndDate={poolData.pledgeEndDate}
              contributionStartDate={poolData.contributionStartDate}
              contributionEndDate={poolData.contributionEndDate}
              fullWidth={true}
              isGiveaway={isGiveawayPool}
              userTier={getProfileData?.learnpad_tier}
              isExternalProject={isExternalProject}
            />
          </Grid>
          {poolData.status === "whitelist_open" &&
            lockedGains > 0 &&
            lockedGains < 1000 && (
              <Grid
                item
                justifyContent="center"
                style={{ textAlign: "center" }}
              >
                <Typography className={classes.backers}>
                  <p
                    style={{
                      display: "inline",
                      cursor: "pointer",
                      alignItems: "center",
                    }}
                    onClick={() => GainsScreen()}
                  >
                    <FaInfoCircle /> Stake additional{" "}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: `${1000 - lockedGains} GAINS`,
                      }}
                    />{" "}
                    at least to participate.
                  </p>
                </Typography>
              </Grid>
            )}
        </Grid>
      </CardActions>
      {showQuizVideoModal && (
        <QuizVideosModal
          open={showQuizVideoModal}
          setOpen={(value: boolean) => toggleQuizVideoModal(value)}
          poolId={poolData.id}
        />
      )}
    </Card>
  );
};

export default PoolDetailCard;
